import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Avatar } from '@material-ui/core';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import {
	Apps as AppsIcon,
	Storage as StorageIcon,
	Cloud as CloudIcon,
	Dashboard as DashboardIcon,
	Settings as SettingsIcon,
	Description as DescriptionIcon,
	Code as CodeIcon,
	Star as StarIcon
} from '@material-ui/icons';
import * as MarketplaceActions from '../../marketplace/store/actions';
import TerminalDialog from './TerminalDialog';
import CampaignShowcaseDialog from './CampaignShowcaseDialog';const useStyles = makeStyles(theme => ({
fadeOverlay: {
position: 'fixed',
left: '50%',
transform: 'translateX(-50%)',
pointerEvents: 'none',
zIndex: 998,
width: '100vw',
height: '300px',
transition: 'bottom 0.3s ease, opacity 0.4s ease'
},
fadeOverlayDark: {
background: 'radial-gradient(ellipse 600px 180px at center bottom, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.85) 25%, rgba(15, 23, 42, 0.6) 45%, rgba(15, 23, 42, 0.3) 65%, rgba(15, 23, 42, 0.1) 80%, transparent 100%)'
},
fadeOverlayLight: {
background: 'transparent'
},
fadeOverlayHidden: {
opacity: 0
},
dockContainer: {
position: 'fixed',
left: '50%',
transform: 'translateX(-50%)',
zIndex: 999,
padding: '12px',
backdropFilter: 'blur(20px)',
borderRadius: '16px',
display: 'flex',
gap: '8px',
alignItems: 'flex-end',
transition: 'bottom 0.3s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
[theme.breakpoints.down('sm')]: {
padding: '8px',
gap: '6px'
}
},
dockContainerDark: {
backgroundColor: '#1B2638',
border: '1px solid rgba(255, 255, 255, 0.08)',
boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(255, 255, 255, 0.05)'
},
dockContainerLight: {
backgroundColor: '#1B2638',
border: '1px solid rgba(255, 255, 255, 0.08)',
boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(255, 255, 255, 0.05)'
},
dockHidden: {
transform: 'translateX(-50%) translateY(calc(100% - 20px))'
},
appIcon: {
width: 56,
height: 56,
borderRadius: '12px',
backgroundColor: '#334155',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
border: '1px solid rgba(255, 255, 255, 0.05)',
'&:hover': {
transform: 'scale(1.15) translateY(-8px)',
backgroundColor: '#475569',
boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)'
},
'&:active': {
transform: 'scale(1.05) translateY(-4px)'
},
[theme.breakpoints.down('sm')]: {
width: 48,
height: 48
}
},
icon: {
fontSize: 28,
color: '#e2e8f0',
[theme.breakpoints.down('sm')]: {
fontSize: 24
}
},
separator: {
width: '1px',
height: 40,
backgroundColor: 'rgba(255, 255, 255, 0.1)',
margin: '0 4px',
alignSelf: 'center',
[theme.breakpoints.down('sm')]: {
height: 32
}
},
tooltip: {
backgroundColor: 'rgba(15, 23, 42, 0.95)',
color: '#f8fafc',
fontSize: '13px',
fontWeight: 500,
padding: '6px 12px',
borderRadius: '6px',
backdropFilter: 'blur(10px)'
}
}));

const PinnedApplicationsSection = ({ workshops = [], alwaysVisible = false, campaignData = null }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const [bottomPosition, setBottomPosition] = useState(24);
	const [isVisible, setIsVisible] = useState(true);
	const [isHovering, setIsHovering] = useState(false);
	const [terminalOpen, setTerminalOpen] = useState(false);
	const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
	const [localCampaignData, setLocalCampaignData] = useState(campaignData);

	// Get pinned applications and user session from Redux
	const pinnedApplications = useSelector(({ dashboard }) => 
		dashboard?.userPreferences?.pinnedApplications || []
	);
	const userSession = useSelector(({ auth }) => auth.user);
	const clusters = useSelector(({ clusters }) => clusters.cluster.list);
	const themeMode = useSelector(({ dashboard }) => dashboard?.userPreferences?.themeMode || 'dark');
const [resolvedPinnedApps, setResolvedPinnedApps] = useState([]);

// Get marketplace data from Redux store (loaded via authenticated API)
const marketplaceData = useSelector(state =>
	state.marketplace && state.marketplace.list
		? state.marketplace.list
		: []
);

// Load marketplace data if not already in store
useEffect(() => {
	if (!marketplaceData || marketplaceData.length === 0) {
		dispatch(MarketplaceActions.getMarketplaceStacks());
	}
}, [dispatch]);

// Resolve pinned app IDs to full app objects from marketplaceData
useEffect(() => {
	if (!marketplaceData.length) return;
	
	const resolved = pinnedApplications.map(pinnedApp => {
		// Handle both legacy format (objects) and new format (IDs)
		if (typeof pinnedApp === 'object' && pinnedApp !== null) {
			// Legacy format: already an object, use as-is
			return pinnedApp;
		} else {
			// New format: just an ID, look up in marketplaceData
			const appId = pinnedApp.toString();
			const app = marketplaceData.find(a => (a.id || a._id) === appId);
			if (app) {
				return app;
			} else {
				console.warn(`Pinned app with ID '${appId}' not found in marketplaceData`);
				return null;
			}
		}
	}).filter(app => app !== null); // Remove nulls (apps not found)
	
	setResolvedPinnedApps(resolved);
}, [pinnedApplications, marketplaceData]);

	const hasWorkshops = workshops && workshops.length > 0;

	useEffect(() => {
		// Only auto-hide if there are workshops AND alwaysVisible is false
		if (hasWorkshops && !alwaysVisible) {
			const hideTimer = setTimeout(() => {
				setIsVisible(false);
			}, 3000);

			return () => clearTimeout(hideTimer);
		}
	}, [hasWorkshops, alwaysVisible]);useEffect(() => {
	const calculatePosition = () => {
		// Find footer element by id
		const footer = document.getElementById('fuse-footer');
		
		if (footer) {
			const footerRect = footer.getBoundingClientRect();
			const windowHeight = window.innerHeight;
			const footerTop = footerRect.top;
			
			// If footer is visible in viewport (footerTop is less than window height)
			if (footerTop < windowHeight) {
				// Calculate distance from bottom of window to top of footer, plus 5px gap
				const distanceFromBottom = windowHeight - footerTop + 5;
				setBottomPosition(distanceFromBottom);
			} else {
				// Footer not visible, use default position
				setBottomPosition(24);
			}
		} else {
			setBottomPosition(24);
		}
	};

	// Calculate on mount and on scroll/resize
	calculatePosition();
	window.addEventListener('scroll', calculatePosition);
	window.addEventListener('resize', calculatePosition);

	// Recalculate periodically in case content changes
	const interval = setInterval(calculatePosition, 500);

	return () => {
		window.removeEventListener('scroll', calculatePosition);
		window.removeEventListener('resize', calculatePosition);
		clearInterval(interval);
	};
}, []);

// Default system apps
const systemApps = [
	{ id: 'apps', name: 'Applications', icon: <AppsIcon className={classes.icon} />, href: '/heidi-apps' }
];

// Add star icon for campaign users (to reopen welcome dialog)
if (userSession?.campaignId) {
	systemApps.push({ id: 'campaign', name: 'Campaign Info', icon: <StarIcon className={classes.icon} />, isCampaign: true });
}

systemApps.push({ id: 'separator' });

// Add terminal only for admin users
if (userSession?.role?.includes('admin')) {
	systemApps.push({ id: 'terminal', name: 'Terminal', icon: <CodeIcon className={classes.icon} />, isTerminal: true });
}

// Combine pinned apps with system apps
const allApps = [
	...resolvedPinnedApps.map(app => ({
		id: app.id || app._id,
		name: app.name,
		icon: (app.logo || app.icon) ? (
			<Avatar 
				src={`/assets/images/logos/${app.logo || app.icon}`} 
				alt={app.name}
				style={{ width: 28, height: 28 }}
			/>
		) : <AppsIcon className={classes.icon} />,
		isPinned: true,
		data: app
	})),
	...(resolvedPinnedApps.length > 0 ? [{ id: 'separator-pinned' }] : []),
	...systemApps
];

	const handleAppClick = async (app) => {
		// Handle campaign info dialog
		if (app.isCampaign) {
			// Fetch campaign data if not already loaded
			if (!localCampaignData && userSession?.campaignId) {
				try {
					const response = await axios.get(`${API_BASE_URL}/api/campaigns/me`);
					setLocalCampaignData(response.data);
				} catch (error) {
					console.error('Error fetching campaign data:', error);
				}
			}
			setCampaignDialogOpen(true);
			return;
		}	// Handle terminal dialog
	if (app.isTerminal) {
		setTerminalOpen(true);
		return;
	}

	if (app.isPinned && app.data) {
		const appData = app.data;
				
		if (appData.action === 'Launch') {
			// Handle web platform apps
			if (appData.platform === 'web') {
				if (appData.config?.web_app_url) {
					let launchUrl = appData.config.web_app_url;
					
					// Add SSO token for partner apps that have SSO enabled
					if (appData.config?.sso_enabled && userSession) {
						try {
							const ssoToken = await dispatch(MarketplaceActions.generateMarketplaceSSO(appData.config));
							const separator = launchUrl.includes('?') ? '&' : '?';
							launchUrl = `${launchUrl}${separator}heidi_token=${ssoToken}`;
						} catch (error) {
							console.error('Failed to generate SSO token:', error);
						}
					}
					
					window.open(launchUrl, '_blank');
				}
				return;
			}
			
			// Handle cluster-app platform
			if (appData.platform === 'cluster-app') {
				let userAvailableClusters = clusters?.filter(cluster => {
					return cluster.info?.state === 'available' && 
						   cluster.user?.includes(userSession?.username);
				});
				
				
				if (!userAvailableClusters || userAvailableClusters.length === 0) {
					console.warn('No available clusters found - redirecting to heidi-apps');
					history.push('/heidi-apps');
					return;
				}
				
				// If only one cluster available, launch directly
				if (userAvailableClusters.length === 1) {
					const cluster = userAvailableClusters[0];
					let clusterCommand = appData.config?.['cluster-command'] || '';
					
					// Generate SSO token if needed
					if (appData.config?.sso_enabled && userSession && clusterCommand) {
						try {
							const ssoToken = await dispatch(MarketplaceActions.generateMarketplaceSSO(appData.config));
							const fullUrl = `${appData.config.web_app_url}?heidi_token=${ssoToken}`;
							const encodedUrl = btoa(fullUrl);
							clusterCommand = clusterCommand.replace('WEB_APP_URL_WITH_SSO_TOKEN', encodedUrl);
						} catch (error) {
							console.error('Failed to generate SSO token:', error);
						}
					}
					
					// Navigate to cluster access tab with VNC auto-launch
					const searchParams = new URLSearchParams({
						autoLaunchVNC: 'true',
						clusterApp: 'true',
						clusterCommand: encodeURIComponent(clusterCommand)
					});
					
					const url = `/clusters/${cluster.id}?${searchParams.toString()}#access`;
					history.push(url);
					return;
				} else {
					history.push('/heidi-apps');
					return;
				}
			}
			
			// Handle AWS platform
			if (appData.platform === 'aws') {
				// If app has supportedProviders, go to cluster creation with provider picker
				if (appData.supportedProviders && appData.supportedProviders.length > 0) {
					dispatch(MarketplaceActions.setStack(appData));
					history.push('/clusters');
					return;
				}
				history.push('/heidi-apps');
				return;
			}
			
			// For other cloud platforms (gcp, azure, ibm, oracle) - or any platform with supportedProviders
			if (appData.supportedProviders && appData.supportedProviders.length > 0) {
				dispatch(MarketplaceActions.setStack(appData));
				history.push('/clusters');
				return;
			}

			dispatch(MarketplaceActions.setStack(appData));
			history.push('/clusters');
		} else {
			history.push('/heidi-apps');
		}
	} else if (app.href) {
		history.push(app.href);
	}
};

const handleMouseEnter = () => {
	setIsHovering(true);
	setIsVisible(true);
};

const handleMouseLeave = () => {
	setIsHovering(false);
	// Only hide if there are workshops (don't block workshop area)
	if (hasWorkshops) {
		// Delay hiding when mouse leaves
		setTimeout(() => {
			if (!isHovering) {
				setIsVisible(false);
			}
		}, 500);
	}
};

return (
<>
<TerminalDialog
	open={terminalOpen}
	onClose={() => setTerminalOpen(false)}
	themeMode={themeMode}
/>
<CampaignShowcaseDialog
	open={campaignDialogOpen}
	onClose={() => setCampaignDialogOpen(false)}
	campaignData={localCampaignData}
/>
{/* Fade overlay */}
<div 
	className={`${classes.fadeOverlay} ${themeMode === 'dark' ? classes.fadeOverlayDark : classes.fadeOverlayLight} ${hasWorkshops && !isVisible && !isHovering ? classes.fadeOverlayHidden : ''}`}
	style={{ bottom: `${bottomPosition - 20}px` }}
/>
{/* Dock container */}
<div 
	className={`${classes.dockContainer} ${themeMode === 'dark' ? classes.dockContainerDark : classes.dockContainerLight} ${hasWorkshops && !isVisible && !isHovering ? classes.dockHidden : ''}`}
	style={{ bottom: `${bottomPosition}px` }}
	onMouseEnter={handleMouseEnter}
	onMouseLeave={handleMouseLeave}
>
{allApps.map((app) => {
if (app.id === 'separator' || app.id === 'separator-pinned') {
return <div key={app.id} className={classes.separator} />;
}

return (
<Tooltip
key={app.id}
title={app.name}
placement="top"
classes={{ tooltip: classes.tooltip }}
arrow
>
<div
className={classes.appIcon}
onClick={() => handleAppClick(app)}
role="button"
tabIndex={0}
onKeyPress={(e) => e.key === 'Enter' && handleAppClick(app)}
					>
						{app.icon}
					</div>
				</Tooltip>
			);
		})}
		</div>
		</>
	);
};

// Memoize to prevent unnecessary re-renders
// This component only depends on user's pinned apps from Redux, not on dashboard metrics
export default React.memo(PinnedApplicationsSection);
